import { useEffect, useImperativeHandle, useRef, useState } from "react";

import { GridEventListener, useGridApiRef } from "@mui/x-data-grid-pro";

import { UseDataGridParams } from "./DataGrid.types";

export const useDataGrid = <R>({
  ref,
  containerHeight,
  autoHeight: defaultAutoHeight,
}: UseDataGridParams<R>) => {
  const apiRef = useGridApiRef();
  const dataGridRef = useRef<HTMLDivElement | null>(null);
  const [autoHeight, setAutoHeight] = useState(!!defaultAutoHeight);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        dataGridRef: dataGridRef.current,
        apiRef: apiRef.current,
        autoHeight,
        setAutoHeight: (v: boolean) => {
          setAutoHeight(v);
        },
      };
    }) as unknown as () => R,
    [autoHeight]
  );

  useEffect(() => {
    if (typeof containerHeight !== "number") return;
    const onStateChangeEvent: GridEventListener<"stateChange"> = (params) => {
      if (params.rows.loading) return;
      const dataGrid = dataGridRef.current;
      if (!dataGrid) return;
      const headerElm = dataGrid.querySelector(
        ".MuiDataGrid-columnHeaders"
      ) as HTMLElement;
      const footerElm = dataGrid.querySelector(
        ".MuiDataGrid-footerContainer"
      ) as HTMLElement;
      const pinnedRows = dataGrid.querySelectorAll(
        ".MuiDataGrid-pinnedRows"
      ) as unknown as HTMLElement[];
      let fixedHeight = 0;
      fixedHeight += headerElm?.offsetHeight ?? 0;
      fixedHeight += footerElm?.offsetHeight ?? 0;
      for (const i of Array.from(pinnedRows ?? [])) {
        fixedHeight += i?.offsetHeight ?? 0;
      }
      setAutoHeight(
        Number(params?.rowsMeta?.currentPageTotalHeight ?? 0) <=
          containerHeight - fixedHeight
      );
    };
    // eslint-disable-next-line consistent-return
    return apiRef?.current?.subscribeEvent?.("stateChange", onStateChangeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerHeight]);

  return {
    autoHeight,
    apiRef,
    dataGridRef,
  };
};
